/**
 * `client/src/audio/beatmap.ts` — la grille de temps d'un morceau, et rien d'autre.
 *
 * **Module PUR** : aucun DOM, aucun React, aucune Web Audio API, aucune horloge. On lui donne
 * un JSON et un instant en millisecondes, il répond. C'est délibéré, et c'est ce qui le rend
 * vérifiable par `client/scripts/verif-audio.ts` — le client n'a pas de runner de tests
 * (SPEC-UI §1.1, liste de dépendances fermée), donc **tout ce qui calcule ici doit pouvoir se
 * vérifier sans navigateur**, exactement comme les `scoring.ts` des mini-jeux avant lui.
 *
 * ## Le format
 *
 * Produit par `scripts/beatmap.ts` (détection d'onsets, PLAN §4.5 / M4.7) :
 *
 * ```json
 * { "version": 1, "id": "rue_fanfare", "bpm": 81.9, "offsetMs": 302,
 *   "durationMs": 84869, "contraste": 2.43, "verifie_a_l_oreille": false,
 *   "beats": [302, 1035, …], "downbeats": [302, 3233, …] }
 * ```
 *
 * `beats` fait foi ; `bpm` et `offsetMs` sont la même information sous forme de grille, gardés
 * parce qu'ils se lisent d'un coup d'œil et parce que `choisirPiste` en a besoin.
 *
 * ## 🔴 La règle qui prime sur tout le reste, ici
 *
 * **Le son n'est jamais une condition de réussite.** ⟦A-22⟧ exige que la réussite dépende de la
 * compétence ; un geste qu'on ne peut réussir qu'à l'oreille exclurait tout joueur qui coupe le
 * son, joue dans le train, ou n'entend pas. Une beatmap sert donc à **décorer, souligner,
 * accompagner** — jamais à décider. Concrètement, et c'est une règle de relecture :
 *
 * > Si le retrait de la musique change un score, une réussite ou une durée, le branchement est
 * > faux — pas la beatmap.
 *
 * La conséquence de conception est que **les consommateurs interrogent leur propre horloge**,
 * et demandent seulement à ce module « où en est la mesure ? » pour synchroniser une pulsation
 * visuelle. Jamais l'inverse. Une musique qui ne démarre pas (fichier absent, navigateur muet,
 * onglet en arrière-plan) laisse alors la séquence se dérouler normalement, en silence.
 */

/** Une grille de temps relue et validée. Les instants sont en ms depuis le début du fichier. */
export interface Beatmap {
  readonly id: string;
  readonly bpm: number;
  readonly offsetMs: number;
  readonly durationMs: number;
  /** Tous les temps. Croissants, jamais vide. */
  readonly beats: readonly number[];
  /** Les temps forts (un sur quatre). Sous-ensemble de `beats`. */
  readonly downbeats: readonly number[];
  /** Contraste mesuré par le script. Sous 1,6 la grille est douteuse. */
  readonly contraste: number;
  /** `false` tant qu'un humain n'a pas écouté (vérification manuelle, PLAN §4.5). */
  readonly verifieALOreille: boolean;
}

function nombre(o: Record<string, unknown>, cle: string): number | null {
  const v = o[cle];
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/**
 * Relit un JSON de beatmap. **Ne lance jamais** : un fichier absent, tronqué ou d'une version
 * future rend `null`, et l'appelant continue sans musique calée. C'est la même politique que
 * `SoundProvider` pour un id absent (SPEC-UI §7.6) — l'audio ne fait pas tomber un écran.
 */
export function lireBeatmap(brut: unknown): Beatmap | null {
  if (typeof brut !== "object" || brut === null) return null;
  const o = brut as Record<string, unknown>;
  if (nombre(o, "version") !== 1) return null;

  const id = typeof o.id === "string" ? o.id : null;
  const bpm = nombre(o, "bpm");
  const offsetMs = nombre(o, "offsetMs");
  const durationMs = nombre(o, "durationMs");
  if (id === null || bpm === null || offsetMs === null || durationMs === null) return null;
  if (bpm <= 0 || durationMs <= 0) return null;

  const beats = Array.isArray(o.beats)
    ? o.beats.filter((b): b is number => typeof b === "number" && Number.isFinite(b))
    : [];
  if (beats.length === 0) return null;
  // La grille doit être croissante : une beatmap désordonnée casserait toute recherche par
  // dichotomie, et se remarquerait comme un décalage aléatoire plutôt que comme une erreur.
  for (let i = 1; i < beats.length; i++) if (beats[i]! <= beats[i - 1]!) return null;

  const downbeats = Array.isArray(o.downbeats)
    ? o.downbeats.filter((b): b is number => typeof b === "number" && Number.isFinite(b))
    : [];

  return {
    id,
    bpm,
    offsetMs,
    durationMs,
    beats,
    downbeats,
    // ⟦A-76⟧ `confiance` est la clé qu'écrivait le script d'⟦A-71⟧ pour la même mesure : sans ce
    // repli, une grille de cette époque retombait sur 0 et passait pour douteuse en silence.
    contraste: nombre(o, "contraste") ?? nombre(o, "confiance") ?? 0,
    verifieALOreille: o.verifie_a_l_oreille === true,
  };
}

/** Durée d'un temps, en ms. */
export function periodeMs(bm: Beatmap): number {
  return 60_000 / bm.bpm;
}

/**
 * Index du dernier temps atteint à l'instant `tMs` — `-1` avant le premier.
 *
 * Dichotomie : appelée à chaque frame par une pulsation visuelle, une recherche linéaire sur
 * 200 temps se verrait au profileur bien avant de se voir à l'écran, mais autant ne pas la
 * poser là.
 */
export function indexDuBeat(bm: Beatmap, tMs: number): number {
  const b = bm.beats;
  if (tMs < b[0]!) return -1;
  let bas = 0;
  let haut = b.length - 1;
  while (bas < haut) {
    const milieu = Math.ceil((bas + haut) / 2);
    if (b[milieu]! <= tMs) bas = milieu;
    else haut = milieu - 1;
  }
  return bas;
}

/** Instant du prochain temps après `tMs`, ou `null` si le morceau est fini. */
export function prochainBeat(bm: Beatmap, tMs: number): number | null {
  const i = indexDuBeat(bm, tMs);
  return bm.beats[i + 1] ?? null;
}

/**
 * Position dans le temps courant, de 0 (on vient de taper) à 1 (le prochain arrive).
 *
 * C'est **la** fonction que les écrans utilisent : une pulsation, un halo qui respire, une
 * plaque qui tressaille sur les temps forts. Hors morceau, elle rend 0 — donc l'animation
 * s'immobilise au lieu de sauter, et l'écran reste lisible en silence.
 */
export function phaseDuBeat(bm: Beatmap, tMs: number): number {
  const i = indexDuBeat(bm, tMs);
  if (i < 0 || i >= bm.beats.length - 1) return 0;
  const debut = bm.beats[i]!;
  const fin = bm.beats[i + 1]!;
  return fin > debut ? (tMs - debut) / (fin - debut) : 0;
}

/** `true` si l'instant `tMs` tombe sur un temps fort, à `toleranceMs` près. */
export function surTempsFort(bm: Beatmap, tMs: number, toleranceMs = 60): boolean {
  for (const d of bm.downbeats) {
    if (Math.abs(d - tMs) <= toleranceMs) return true;
    if (d > tMs + toleranceMs) return false;
  }
  return false;
}

/**
 * Cale une durée voulue sur un nombre entier de temps.
 *
 * Le service rendu à un écran chorégraphié : « je veux que cette phase dure à peu près 4 s » →
 * « alors fais-la durer 3 temps, ça tombe à 3,66 s et ça respire avec la musique ». Si la
 * musique manque, l'écran garde `voulueMs` et personne ne voit la différence.
 */
export function calerSurLesTemps(bm: Beatmap | null, voulueMs: number): number {
  if (!bm) return voulueMs;
  const p = periodeMs(bm);
  const temps = Math.max(1, Math.round(voulueMs / p));
  return Math.round(temps * p);
}

/** Une piste telle que le manifeste la décrit, du point de vue du choix de tempo. */
export interface PisteDisponible {
  readonly id: string;
  /** Le BPM natif du fichier, celui que la beatmap a mesuré. */
  readonly bpm: number;
}

/**
 * Choisit la piste la plus proche d'un tempo voulu, et l'étirement qui l'y amène.
 *
 * ⚠️ **⟦C50 · A-169 §2⟧ Le Montage ne l'appelle plus.** Ni le serveur (qui tire dans les patterns
 * pré-calculés sur l'audio, `server/src/atelier/patterns.ts`) ni le bac à sable (qui tire dans le
 * même fichier, `client/src/atelier/CouperDansLeRythme/pattern.ts`) : la musique n'est plus étirée,
 * l'écart entre deux carrés vient du pattern choisi, plus d'un tempo imposé. Les grilles de ce fichier
 * ne servent plus au Montage non plus (elles étaient fausses : voir
 * `docs/chantiers/C50-le-montage-cale-sur-la-musique.md`). La fonction reste, avec sa vérification
 * (`verif-audio.ts`), pour un jour où une musique devra suivre un tempo imposé.
 *
 * ## Pourquoi cette fonction existe, et pourquoi il faut 6 à 8 morceaux
 *
 * ⟦Avant C50⟧ Le Montage (SPEC-UI §5.3) générait ses cuts **à partir d'un BPM**, qui monte de 81 à 122 selon
 * le palier d'exigence (§4.2). Une musique dont la grille ne tombe pas sur ces cuts est pire
 * que pas de musique du tout : elle *ment* au joueur sur l'endroit où couper.
 *
 * Il faut donc étirer la piste — `playbackRate` — pour amener son tempo natif sur celui du
 * pattern. Mais l'étirement d'un lecteur audio déplace aussi la hauteur : à ×1,36 un morceau
 * part d'un demi-ton et demi vers le haut, et ça s'entend franchement. D'où la règle :
 *
 * 🔑 **On ne couvre pas 81→122 BPM avec un morceau, on le couvre avec six.** Chacun sert la
 * tranche autour de son tempo natif, et l'étirement reste sous `ETIREMENT_MAX`. C'est
 * exactement la raison pour laquelle PLAN §4.5 demande « 6-8 morceaux » et pas un seul — la
 * raison n'était écrite nulle part, elle l'est maintenant.
 *
 * Au-delà de la tolérance, on rend `null` : **mieux vaut le silence qu'un morceau faux.**
 */
export const ETIREMENT_MAX = 0.12;

export function choisirPiste(
  bpmVoulu: number,
  pistes: readonly PisteDisponible[],
): { id: string; playbackRate: number } | null {
  if (!(bpmVoulu > 0) || pistes.length === 0) return null;
  let meilleure: { id: string; playbackRate: number; ecart: number } | null = null;
  for (const p of pistes) {
    if (!(p.bpm > 0)) continue;
    const taux = bpmVoulu / p.bpm;
    // L'écart se mesure en octaves (log2), pas en BPM : passer de 80 à 90 et de 110 à 120,
    // c'est dix BPM dans les deux cas et pas du tout le même étirement à l'oreille.
    const ecart = Math.abs(Math.log2(taux));
    if (meilleure === null || ecart < meilleure.ecart) {
      meilleure = { id: p.id, playbackRate: taux, ecart };
    }
  }
  if (meilleure === null) return null;
  if (Math.abs(meilleure.playbackRate - 1) > ETIREMENT_MAX) return null;
  return { id: meilleure.id, playbackRate: meilleure.playbackRate };
}
